import { Text, Fill, Icon, Stroke, Style } from "ol/style";
import { Switchboard } from "../../lib/events";
import { async_forEach, get_sequential_diff } from "../../lib/misc/array";
import { None } from "../../lib/misc/option";
import { BaseActionWithNav } from "./BaseActionWithNav";
import { Popup } from "../interactions/popup/Popup";
import { boundingExtent } from "ol/extent";
class ResultTable {
    constructor(columns, data) {
        this.strapToDataMap = new Map();
        this.columns = columns;
        this.data = data;
        const strapIndex = this.strap_index;
        if (strapIndex !== -1) {
            data.forEach(row => {
                const strapValue = row[strapIndex];
                this.strapToDataMap.set(strapValue, row);
            });
        }
    }
    get strap_index() {
        const col = this.columns.find(c => c.displayName.toLowerCase() === "strap");
        return col ? parseInt(col.name) : -1;
    }
    get ids() {
        const strapIndex = this.strap_index;
        if (strapIndex === -1)
            return [];
        return this.data.map(row => row[strapIndex]);
    }
    get_data_for(strap) {
        let schema = {};
        this.columns.filter(c => c.visible).forEach(col => schema[col.displayName] = null);
        const row = this.strapToDataMap.get(strap);
        if (!row)
            return;
        for (const col of Object.keys(schema)) {
            const column = this.columns.find(c => c.displayName === col);
            if (column.name !== null) {
                schema[col] = row[parseInt(column.name)];
            }
        }
        return schema;
    }
}
var pinnedTooltips = [];
export class SetMarkersAction extends BaseActionWithNav {
    constructor(map, p_layer, m_layer) {
        super('SET MARKERS');
        this.map = map;
        this.p_layer = p_layer;
        this.m_layer = m_layer;
        this.context = "selection";
        this.emitter = Switchboard.get_emitter("CONTEXT");
        this.receiver = Switchboard.get_receiver("CONTEXT");
        this.prior_universe = [];
        this.prior_selected = [];
        this.is_initial = false;
        this.handleTooltipAndPinDisplay = (event) => {
            var _a, _b, _c;
            if (event.dragging)
                return;
            Popup.removeUnpinnedTooltips(event, pinnedTooltips);
            const pixel = this.map.getEventPixel(event.originalEvent);
            let features = this.map.get_features_at_pixel(pixel);
            let parcelFeatures = features.filter(([feature, layer]) => layer === this.p_layer.raw).map(([feature]) => feature);
            const markerFeatures = features.filter(([feature]) => feature.get('type') === 'marker').map(([feature]) => feature);
            const allFeatures = [...markerFeatures, ...parcelFeatures];
            if (allFeatures.length === 0)
                return;
            const feature = allFeatures[0];
            const data = feature.getProperties();
            if (data) {
                const columnMappings = {
                    dsp_strap: 'Parcel',
                    addr: 'Addr',
                    adj_price: 'ADJ Price',
                };
                const columns = Object.keys(columnMappings);
                const filteredData = columns.reduce((result, column) => {
                    if (data.hasOwnProperty(column)) {
                        result[columnMappings[column]] = data[column];
                    }
                    return result;
                }, {});
                const parcelData = data._comp_parcel_data_dict || {};
                filteredData['Land Size'] = ((_a = parcelData['Land Size']) === null || _a === void 0 ? void 0 : _a.val) || 0;
                filteredData['Effective Area'] = ((_c = (_b = parcelData['Building Effective Area']) === null || _b === void 0 ? void 0 : _b.val) === null || _c === void 0 ? void 0 : _c.toString()) || 0;
                if (filteredData['Parcel']) {
                    const popup = new Popup(feature, this.map);
                    popup.addOrUpdateOverlay(filteredData);
                    const pinButton = popup.setupPinButton(filteredData);
                    if (!pinButton.data('listenerAdded')) {
                        pinButton.on('click', () => {
                            pinnedTooltips = popup.handlePinButtonClick(pinButton, filteredData, pinnedTooltips);
                        });
                        pinButton.data('listenerAdded', true);
                    }
                }
            }
        };
        this.receiver.on('ctx_change', (ctx => {
            if (ctx.name !== this.context) {
                this.reset();
            }
        }));
    }
    initializeMapLayers() {
        this.is_initial = true;
        const defaultLayerNames = ['Basemap', 'Parcels'];
        const layers = this.map.rest_layers.filter((l) => l.is_base);
        const defaultLayer = layers.find((layer) => defaultLayerNames.includes(layer.name));
        if (!defaultLayer) {
            return;
        }
        layers.forEach((layer) => {
            layer.visible = false;
        });
        defaultLayer.visible = true;
        const checkbox = $(`#${defaultLayer.name}`).get(0);
        if (checkbox) {
            checkbox.checked = true;
        }
    }
    reset() {
        this.prior_selected = [];
        this.prior_universe = [];
    }
    validate(message) {
        return super.validate(message)
            && message.selected
            && Array.isArray(message.selected)
            && typeof message.universe === 'string';
    }
    on_valid_message() {
        if (!this.is_initial)
            this.initializeMapLayers();
        this.emitter.emit('ctx_request', 'selection');
    }
    async interpret(message) {
        const resultObj = JSON.parse(message.universe);
        const resultTable = new ResultTable(resultObj.columns, resultObj.data);
        let ids = [message.selected[0], ...resultTable.ids];
        var show = ids.length === message.selected.length;
        let allTooltips = $('[id*=tooltip-]');
        allTooltips.each(function () {
            if (!ids.find(id => `tooltip-${id}` == this.id) || !show) {
                $(this).hide();
                pinnedTooltips = [];
            }
        });
        if (this.p_layer.colorized) {
            this.p_layer.clear();
            this.p_layer.use_base_style();
        }
        let { adds, subs } = get_sequential_diff(this.prior_universe, ids);
        if (adds.length > 0)
            await this.p_layer.add_features_by_ids(adds);
        if (subs.length > 0)
            this.p_layer.remove_features_by_ids(subs);
        this.prior_universe = ids;
        for (let parcel of this.p_layer.features) {
            const strap = parcel.get(this.p_layer.identifier);
            const record = resultTable.get_data_for(strap);
            for (let key in record) {
                parcel.set(key, record[key]);
            }
        }
        let { adds: m_adds, subs: m_subs } = get_sequential_diff(this.prior_selected, message.selected);
        await async_forEach(m_adds, async (strap) => {
            await this.m_layer.add_marker_to_parcel(strap);
            const marker = this.m_layer.markers.find(m => m.get('strap') === strap);
            const markerData = resultTable.get_data_for(strap);
            if (marker && markerData) {
                for (let key in markerData) {
                    marker.set(key, markerData[key]);
                }
            }
        });
        this.zoom_to_extent();
        await async_forEach(m_subs, async (strap) => this.m_layer.remove_marker_from_parcel(strap));
        const comp_marker = this.m_layer.markers.filter(m => m.get('strap') !== message.selected[0]);
        let data = this.p_layer.features.map(x => x.getProperties());
        comp_marker.forEach(c => {
            var _a;
            const strap = c.get('strap');
            let comp_num;
            if (show) {
                comp_num = (_a = data.find(d => d[this.p_layer.identifier] === strap)) === null || _a === void 0 ? void 0 : _a.num;
            }
            const style = this.m_layer.raw.getStyle();
            const imageStyle = style instanceof Style && style.getImage ? style.getImage() : undefined;
            c.setStyle(new Style({
                image: imageStyle,
                text: new Text({
                    text: comp_num ? comp_num.toString() : '',
                    font: '12px Arial',
                    fill: new Fill({ color: '#fff' }),
                    stroke: new Stroke({ color: '#000', width: 3 }),
                    offsetY: -15
                })
            }));
        });
        const selected_marker = this.m_layer.markers.find(m => m.get('strap') === message.selected[0]);
        selected_marker === null || selected_marker === void 0 ? void 0 : selected_marker.setStyle(new Style({
            image: new Icon({
                anchor: [0.5, 1],
                opacity: 1.0,
                scale: 0.05,
                src: '../../../../../../img/gis/map-marker-icon-blue-sel.png'
            })
        }));
        this.p_layer.select_feature(message.selected[0]);
        this.prior_selected = message.selected;
        this.map.on('pointermove', evt => {
            ids = [message.selected[0], ...resultTable.ids];
            show = ids.length === message.selected.length;
            if (show) {
                this.handleTooltipAndPinDisplay(evt);
            }
        });
    }
    zoom_to_extent() {
        let extent;
        if (this.p_layer.features.length === 1) {
            extent = this.p_layer.features[0].getGeometry().getExtent();
        }
        else {
            extent = boundingExtent(this.p_layer.features.map(f => f.getGeometry().getExtent()));
        }
        const padding = [150, 120, 150, 120];
        const mapSize = this.map.getSize();
        const view = this.map.getView();
        const [minX, minY, maxX, maxY] = extent;
        const adjustedExtent = [
            minX - padding[3],
            minY - padding[2],
            maxX + padding[1],
            maxY + padding[0]
        ];
        view.fit(adjustedExtent, { size: mapSize, padding, duration: 200 });
    }
    after_valid_message() {
        return {
            context: '',
            zoom: true,
            center: false,
            fail: false,
            navigate: None.instance()
        };
    }
}
